// ═══════════════════════════════════════════════════════════════════════════
// apiError.js — the sentence to show for an error answer from the main API
// ═══════════════════════════════════════════════════════════════════════════
// No React, no network, no storage: `t` is passed in. Used where the web app
// shows a server's `error`: the help widget (/api/support, App.js) and the AI
// proxy (/api/anthropic, lib/api.js). /api/contact (data/contact.js) and
// /api/events (lib/track.js) ignore their answers by design, so they need
// nothing here.
//
// Express answers these routes with English sentences, and the .NET services
// (feat/dotnet-foundation) copy them word for word: those are shown unchanged.
// The .NET platform also answers codes of its own (dotnet/README.md "The error
// contract", NetrevErrors.cs, SupabaseAuthenticationDefaults.cs), and any other
// bare status as its reason phrase in snake case (409 `conflict`, 502
// `bad_gateway`), so the set is open. A code in the table gets its own
// sentence, any other snake_case word the generic one: a raw code never reaches
// the screen, and neither does a raw catalog key.
//
// Inert while Express answers, with one exception: every Express body on these
// routes has a space or a capital, except Node's own `terminated` (undici's
// error when a response body is cut off mid-read), which Express's AI proxy can
// answer as `500 {"error":"terminated"}`. It now reads as the generic sentence.
// apiError.test.js pins both.
//
// The help widget also passes the HTTP status. /api/support relays text it
// didn't write at 5xx, on Express and on .NET alike: Resend's own answer
// ("resend 422: ...", which can quote the user's address), a config detail, an
// exception's message. With the status, a 5xx shows its sentence only when it
// is a code in the table or one of the two crafted sentences below; anything
// else is the generic sentence. lib/api.js passes no status on purpose: the AI
// proxy's 503 and 504 are crafted sentences that AI insight shows as sent.
// ═══════════════════════════════════════════════════════════════════════════

const has = (o, k) => Object.prototype.hasOwnProperty.call(o, k);
// The same shape lib/feedApi.js and lib/feedUi.js accept as a code.
const CODE_RE = /^[a-z][a-z0-9_]{0,63}$/;

// Server string → catalog key. An explicit table, never a key built from the
// server's string: two entries are reason phrases with a space or a capital.
export const API_ERROR_KEYS = Object.freeze({
  "Bad Request": "app.apiErr.bad_request",          // 400: malformed JSON
  Forbidden: "app.apiErr.forbidden",                // 403 with no sentence of its own
  not_found: "app.apiErr.not_found",                // 404
  method_not_allowed: "app.apiErr.method_not_allowed", // 405 (a controller only)
  payload_too_large: "app.apiErr.payload_too_large", // 413
  unsupported_media_type: "app.apiErr.unsupported_media_type", // 415: a body that isn't JSON
  rate_limited: "app.apiErr.rate_limited",          // 429 with no sentence of its own
  internal_error: "app.apiErr.internal_error",      // 500
  db_unavailable: "app.apiErr.db_unavailable",      // 503
  upstream_unavailable: "app.apiErr.upstream_unavailable", // 503: Anthropic or Resend unreachable
  auth_unavailable: "app.apiErr.auth_unavailable",  // 503: Supabase Auth unreachable
  service_unavailable: "app.apiErr.service_unavailable", // 503 with no more specific cause
  timeout: "app.apiErr.timeout",                    // 504: past the route's time limit
});
// Any other code, and an answer with no usable `error` (an HTML page, no body).
export const API_ERROR_UNKNOWN_KEY = "app.apiErr.unknown";
// Client side: the request never got an answer (fetch rejected).
export const API_ERROR_NETWORK_KEY = "app.apiErr.network";
// The 5xx sentences /api/support writes itself, shown as sent when the caller
// passes the status. Exact strings, compared as they come.
export const CRAFTED_5XX_SENTENCES = Object.freeze([
  "Could not send your message. Please try again.",           // 502: Express's fallback; .NET EmailAnswers.CouldNotSend
  "Support is busy right now. Please try again in a moment.", // 503: .NET SupportRules.Busy
]);

const nonBlank = (s) => typeof s === "string" && s.trim() !== "";

// The catalog key for a server's `error` string: its own key for a code in the
// table, the generic key for any other code, null for a sentence (shown as is).
export function apiErrorKey(error) {
  if (typeof error !== "string") return null;
  if (has(API_ERROR_KEYS, error)) return API_ERROR_KEYS[error];
  return CODE_RE.test(error) ? API_ERROR_UNKNOWN_KEY : null;
}

// The text to show for a parsed error body (or null when it wasn't JSON).
// • a code → its sentence through t(); an unknown code → the generic sentence;
// • with options.status from 500 to 599: a crafted sentence → unchanged,
//   anything else → generic (the help widget only, see the header);
// • any other non-blank string → unchanged (Express's sentences, and .NET's
//   copies of them);
// • Anthropic's own error, passed through the proxy as { error: { type,
//   message } } → its message (the .NET proxy relies on this distinction);
// • anything else (no body, {}, a blank or non-string error) → generic.
// Without options (or without a status) the answer is what it always was.
export function apiErrorText(data, t, options) {
  const error = data && typeof data === "object" ? data.error : undefined;
  const key = apiErrorKey(error);
  if (key) return t(key);
  const status = options && typeof options === "object" ? options.status : undefined;
  if (typeof status === "number" && status >= 500 && status < 600) {
    return CRAFTED_5XX_SENTENCES.includes(error) ? error : t(API_ERROR_UNKNOWN_KEY);
  }
  if (nonBlank(error)) return error;
  if (error && typeof error === "object" && nonBlank(error.message)) return error.message;
  return t(API_ERROR_UNKNOWN_KEY);
}
